import { Accordion } from 'react-bootstrap';
import { Ai2Layout } from '../../layout/Ai2Layout';
import './styles/settings-page.css';
import { useEffect, useState } from 'react';
import { ManageUserTable } from '../../components/Users/ManageUserTable';
import { ManagePermissions } from '../../components/Users/ManagePermissions';
import { ManageTask } from '../../components/Users/ManageTask';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useAllRoles } from '../../../context/RoleContext';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import { SystemPermission } from '../../../context/PermissionsContext';
import { useLoggedInUser } from '../../../context';

export const SystemSettingsTabs = Object.freeze({
  MANAGE_USERS: 'manage-users',
  MANAGE_PERMISSIONS: 'manage-permissions',
  MANAGE_TASKS: 'manage-tasks',
});

export const SystemSettings = () => {
  const [activeTab, setActiveTab] = useState('');
  const {
    tenancyRoles,
    permissionKeyValuePair,
    isLoading: isGetting,
    fetchAllRoles,
  } = useAllRoles();
  const { clinics } = useSelector((state: RootState) => state.clinicianProfile);
  const { loggedInUser } = useLoggedInUser();
  const allTeams = clinics.filter(
    (clinic) => clinic.organisation_unit === 'team',
  );

  const allClinicianIdsInTeams = Array.from(
    new Set(
      allTeams.flatMap((team) =>
        team.caremapprClinicians?.map((clinician) => clinician.user_id || []),
      ),
    ),
  ).filter(Boolean) as number[];

  const isInAnyTeam = allClinicianIdsInTeams.includes(
    loggedInUser?.userId || 0,
  );

  useEffect(() => {
    if (isEmpty(tenancyRoles) && isEmpty(permissionKeyValuePair)) {
      fetchAllRoles();
    }
  }, [tenancyRoles, permissionKeyValuePair]);

  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  const canManagePermissions =
    !selectedTenancy?.permissionsSetup ||
    (selectedTenancy?.permissionsSetup &&
      selectedTenancy?.userPermissions &&
      !!(selectedTenancy?.userPermissions)[SystemPermission.MANAGE_PERMISSION]);

  const canManageUsers =
    !selectedTenancy?.permissionsSetup ||
    (selectedTenancy?.permissionsSetup &&
      selectedTenancy?.userPermissions &&
      !!(selectedTenancy?.userPermissions)[SystemPermission.ASSIGN_ROLE]);

  const canManageTasks =
    !selectedTenancy?.permissionsSetup ||
    (selectedTenancy?.permissionsSetup &&
      selectedTenancy?.userPermissions &&
      !!(selectedTenancy?.userPermissions)[SystemPermission.MANAGE_TASK]);

  return (
    <Ai2Layout>
      <div className="settings-page">
        <div className="settings-page-title">System settings</div>
        {isGetting ? (
          <div className="h-100 d-flex justify-content-center align-items-center">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="preference-container">
            <Accordion
              activeKey={activeTab}
              onSelect={(key) => setActiveTab(key as any)} // TODO fix this any!!
            >
              {canManageUsers && (
                <Accordion.Item eventKey={SystemSettingsTabs.MANAGE_USERS}>
                  <Accordion.Header>
                    Manage users
                    {activeTab !== SystemSettingsTabs.MANAGE_USERS ? (
                      <i className="fa fa-plus" aria-hidden="true" />
                    ) : (
                      <i className="fa fa-minus" aria-hidden="true" />
                    )}
                  </Accordion.Header>
                  <Accordion.Body>
                    <ManageUserTable
                      roleResults={{ tenancy_roles: tenancyRoles }}
                      isGetting={isGetting}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              )}
              {canManagePermissions && (
                <Accordion.Item
                  eventKey={SystemSettingsTabs.MANAGE_PERMISSIONS}
                >
                  <Accordion.Header>
                    Manage permissions
                    {activeTab !== SystemSettingsTabs.MANAGE_PERMISSIONS ? (
                      <i className="fa fa-plus" aria-hidden="true" />
                    ) : (
                      <i className="fa fa-minus" aria-hidden="true" />
                    )}
                  </Accordion.Header>
                  <Accordion.Body>
                    <ManagePermissions
                      roleResults={{
                        tenancy_roles: tenancyRoles,
                        permissionKeyValuePair,
                      }}
                      isGetting={isGetting}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              )}
              {canManageTasks && (
                <Accordion.Item eventKey={SystemSettingsTabs.MANAGE_TASKS}>
                  <Accordion.Header>
                    Manage tasks
                    {activeTab !== SystemSettingsTabs.MANAGE_TASKS ? (
                      <i className="fa fa-plus" aria-hidden="true" />
                    ) : (
                      <i className="fa fa-minus" aria-hidden="true" />
                    )}
                  </Accordion.Header>
                  <Accordion.Body>
                    {isInAnyTeam ? (
                      <ManageTask />
                    ) : (
                      <div className="my-4 text-center">
                        You can only add tasks to the teams you are in.
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              )}
            </Accordion>
          </div>
        )}
      </div>
    </Ai2Layout>
  );
};
